import type { QueryFunctionContext } from '@tanstack/react-query';
import { AdminService, AsrProxyAdminService, PromptsService } from 'app/API';
import { cancelOnSignal } from 'app/API/core/CancelablePromise';
import type { adminOrganizationsKeys } from 'app/queries/adminOrganizations/adminOrganizationsKeys';
import type { TCall } from 'app/queries/types/TCall';
import type { TOrganizationModelChoices } from '../types/TOrganizationModelChoices';

type TParams = QueryFunctionContext<ReturnType<typeof adminOrganizationsKeys.modelChoices>>;

const getFulfilledValue = <T>(result: PromiseSettledResult<T>): T | undefined =>
  result.status === 'fulfilled' ? result.value : undefined;

export const createFetchOrganizationModelChoicesQueryFn =
  (call: TCall) =>
  async ({ client, queryKey, signal }: TParams): Promise<TOrganizationModelChoices> => {
    const [{ organizationId }] = queryKey;
    const [modelsResult, promptsResult, promptConfigsResult, asrModelsResult, asrConfigResult] =
      await Promise.allSettled([
        call(() => cancelOnSignal(AdminService.getAvailableOpenAiModels(), signal)),
        call(() => cancelOnSignal(PromptsService.getAll(), signal)),
        call(() => cancelOnSignal(PromptsService.getPromptConfigsForOrg({ oid: organizationId }), signal)),
        call(() => cancelOnSignal(AsrProxyAdminService.models(), signal)),
        call(() => cancelOnSignal(AsrProxyAdminService.getOrganizationConfig({ organizationId }), signal)),
      ]);

    const previous = client.getQueryData<TOrganizationModelChoices>(queryKey);
    const prompts = getFulfilledValue(promptsResult) ?? previous?.prompts ?? [];
    const promptConfigs = getFulfilledValue(promptConfigsResult);
    const asrModels = getFulfilledValue(asrModelsResult);
    const settledSections: Array<[string, PromiseSettledResult<unknown>]> = [
      ['LLM models', modelsResult],
      ['prompts', promptsResult],
      ['prompt model settings', promptConfigsResult],
      ['ASR models', asrModelsResult],
      ['ASR model settings', asrConfigResult],
    ];

    const promptModels: Record<string, string> = Object.fromEntries(
      prompts.map((prompt) => [prompt.slug, promptConfigs ? '' : previous?.promptModels[prompt.slug] ?? '']),
    );
    promptConfigs?.forEach((config) => {
      promptModels[config.promptSlug] = config.model;
    });

    return {
      versions: getFulfilledValue(modelsResult) ?? previous?.versions ?? [],
      prompts,
      promptModels,
      asrDefaultModel: asrModels ? asrModels.default_model ?? '' : previous?.asrDefaultModel ?? '',
      asrModels: asrModels ? asrModels.models.map((model) => model.model_name) : previous?.asrModels ?? [],
      asrModel: asrConfigResult.status === 'fulfilled' ? asrConfigResult.value?.model ?? '' : previous?.asrModel ?? '',
      failedSections: settledSections.filter(([, result]) => result.status === 'rejected').map(([section]) => section),
    };
  };
